import React, { useEffect, useRef, useState } from "react";
import {
    VendorSendSvg,
    VendorCloseSvg,
} from "../../../components/CommonSvg";
import { EMAIL_PATTERN, initials, statusSuffix } from "./ticketUtils";

/* the composer owns the recipient draft: it is mounted only while forwarding,
   so closing it is what throws the half-typed list away */
const ForwardCompose = ({
    ticket,
    original,
    hasMultipleVendors,
    sharing,
    onSend,
    onDiscard,
    showToast,
}) => {
    const [recipients, setRecipients] = useState([]);
    const [recipientDraft, setRecipientDraft] = useState("");

    const toInputRef = useRef(null);
    const composeRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            composeRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            toInputRef.current?.focus();
        }, 60);
        return () => clearTimeout(timer);
    }, []);

    const commitRecipients = (text) => {
        const tokens = String(text || "")
            .split(/[,;\s]+/)
            .map((token) => token.trim())
            .filter(Boolean);

        if (!tokens.length) {
            setRecipientDraft("");
            return;
        }

        const valid = [];
        const invalid = [];
        tokens.forEach((token) => {
            if (EMAIL_PATTERN.test(token)) {
                valid.push(token);
            } else {
                invalid.push(token);
            }
        });

        if (valid.length) {
            setRecipients((prev) => [
                ...prev,
                ...valid.filter(
                    (email, index) =>
                        !prev.includes(email) && valid.indexOf(email) === index
                ),
            ]);
        }

        setRecipientDraft(invalid.join(", "));

        if (invalid.length) {
            showToast(
                invalid.length === 1
                    ? `"${invalid[0]}" is not a valid email`
                    : `${invalid.length} entries are not valid emails`
            );
        }
    };

    const handleRecipientKey = (event) => {
        if (event.key === "Enter" || event.key === "," || event.key === ";") {
            event.preventDefault();
            commitRecipients(recipientDraft);
            return;
        }

        if (event.key === "Backspace" && !recipientDraft) {
            setRecipients((prev) => prev.slice(0, -1));
        }
    };

    const handleRecipientPaste = (event) => {
        const pasted = event.clipboardData?.getData("text");
        if (!pasted) return;
        event.preventDefault();
        commitRecipients(`${recipientDraft} ${pasted}`);
    };

    const handleSend = () => {
        if (sharing) return;

        /* what is still sitting in the input counts too - asking for a comma
           before Send would only lose the last address */
        const pending = recipientDraft
            .split(/[,;\s]+/)
            .map((token) => token.trim())
            .filter((token) => EMAIL_PATTERN.test(token));

        const to = [...new Set([...recipients, ...pending])];

        if (!to.length) {
            showToast('Add an email in "To" first');
            return;
        }
        if (!ticket?.id) {
            showToast("This ticket cannot be forwarded");
            return;
        }

        onSend(to);
    };

    const renderRecipientPill = (email) => (
        <span className="vp-tk-rpill" key={email}>
            <span className="vp-tk-pav" style={{ background: "#69797e" }}>
                {initials(email)}
            </span>
            {email}
            <button
                type="button"
                className="vp-tk-px"
                onClick={() => setRecipients((prev) => prev.filter((x) => x !== email))}
            >
                ×
            </button>
        </span>
    );

    const first = original || {};

    return (
        <div className="vp-tk-compose" ref={composeRef}>
            <div className="vp-tk-ctop">
                <button
                    type="button"
                    className="vp-tk-send-btn"
                    onClick={handleSend}
                    disabled={sharing}
                >
                    <VendorSendSvg />
                    {sharing ? "Sending…" : "Send"}
                </button>
                <span className="vp-tk-ctop-ttl">Forward to vendor staff</span>
                <button
                    type="button"
                    className="vp-tk-discard"
                    onClick={onDiscard}
                >
                    <VendorCloseSvg />
                    Discard
                </button>
            </div>
            <div className="vp-tk-crow">
                <button type="button" className="vp-tk-clabel">
                    To
                </button>
                {recipients.map(renderRecipientPill)}
                <span className="vp-tk-to-wrap">
                    <input
                        ref={toInputRef}
                        className="vp-tk-to-input"
                        type="text"
                        value={recipientDraft}
                        placeholder="Type or paste emails, separated by commas"
                        onChange={(event) => setRecipientDraft(event.target.value)}
                        onKeyDown={handleRecipientKey}
                        onPaste={handleRecipientPaste}
                        onBlur={() => commitRecipients(recipientDraft)}
                    />
                </span>
            </div>
            <div className="vp-tk-crow">
                <input
                    className="vp-tk-subj-input"
                    type="text"
                    value={`[${ticket.no}] ${ticket.subj}`}
                    readOnly
                />
            </div>
            <div className="vp-tk-qcard">
                <div className="vp-tk-qctop">
                    <span className="vp-tk-qcno">#{ticket.no}</span>
                    <span className={`vp-tk-qcst st-${statusSuffix(ticket.status)}`}>
                        {ticket.status || "—"}
                    </span>
                </div>

                <div className="vp-tk-qcsubj">{ticket.subj}</div>

                <div className="vp-tk-qcgrid">
                    {[
                        ["Raised by", first.who],
                        ["Category", ticket.category],
                        ["Vendor", hasMultipleVendors ? ticket.vendorName : ""],
                        ["Priority", ticket.priority],
                        ["Created date", first.dt],
                    ]
                        .filter(([, value]) => value)
                        .map(([label, value]) => (
                            <div className="vp-tk-qcf" key={label}>
                                <i>{label}</i>
                                <b>{value}</b>
                            </div>
                        ))}
                </div>

                {first.body && (
                    <div className="vp-tk-qcdesc">
                        <i>Description</i>
                        <p>{first.body}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForwardCompose;
